import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { AzureMLServiceLinkedService, azureMLServiceAuthTypes } from 'app/model/linkedservice/azure-ml-service.linkedservice';
import { AuthenticationType, ConnectionAuthKind, ITridentResource } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureMLServiceLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                subscriptionId: '*',
                resourceGroupName: '*',
                mlWorkspaceName: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                tenant: '*',
                encryptedCredential: '*',
                authentication: '*'
            }
        }
    }
})
export class AzureMLServiceLinkedServiceAdapter extends LinkedServiceAdapter<AzureMLServiceLinkedService> {

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Model.AzureMLServiceLinkedServiceTypeProperties => {
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        const pathProperties = datasourceReference.path.split(';');
        const credentialType = tridentResource.credentialDetails.credentialType;
        const typeProperties: Model.AzureMLServiceLinkedServiceTypeProperties = {
            subscriptionId: pathProperties[0],
            resourceGroupName: pathProperties[1],
            mlWorkspaceName: pathProperties[2]
        };
        switch (credentialType) {
            case ConnectionAuthKind.ServicePrincipal:
                typeProperties.servicePrincipalId = '000'; //fake ID
                break;
            case ConnectionAuthKind.OAuth2:
                typeProperties.authentication = AuthenticationType.UserAuth;
                break;
            default:
        }
        return typeProperties;
    };

    protected modelConstructor(): new () => AzureMLServiceLinkedService {
        return AzureMLServiceLinkedService;
    }

    protected model(): IModel {
        return AzureMLServiceLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureMLServiceLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureMLServiceLinkedService;

        properties.type = Metamodel.modelFor(AzureMLServiceLinkedService).name;

        if (linkedService.authenticationMethod === azureMLServiceAuthTypes.ServicePrincipalAuthentication) {
            properties.typeProperties = {
                subscriptionId: linkedService.subscriptionId,
                resourceGroupName: linkedService.resourceGroupName,
                mlWorkspaceName: linkedService.mlWorkspaceName,
                servicePrincipalId: linkedService.servicePrincipalId,
                servicePrincipalKey: passwordOrAKVtoSecretBase(linkedService.servicePrincipalKey, this.context),
                tenant: linkedService.tenant
            };
        } else if (linkedService.authenticationMethod === azureMLServiceAuthTypes.MSIAuthentication) {
            properties.typeProperties = {
                subscriptionId: linkedService.subscriptionId,
                resourceGroupName: linkedService.resourceGroupName,
                mlWorkspaceName: linkedService.mlWorkspaceName,
                authentication: 'MSI'
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureMLServiceLinkedService): Promise<AzureMLServiceLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureMLServiceLinkedService;

        linkedService.subscriptionId = properties.typeProperties.subscriptionId;
        linkedService.resourceGroupName = properties.typeProperties.resourceGroupName;
        linkedService.mlWorkspaceName = properties.typeProperties.mlWorkspaceName;
        linkedService.subscriptionId = properties.typeProperties.subscriptionId;
        linkedService.servicePrincipalId = undefined;
        linkedService.servicePrincipalKey = undefined;
        linkedService.tenant = undefined;
        linkedService.authenticationMethod = azureMLServiceAuthTypes.MSIAuthentication;

        if (properties.typeProperties.servicePrincipalId) {
            linkedService.authenticationMethod = azureMLServiceAuthTypes.ServicePrincipalAuthentication;
            linkedService.servicePrincipalId = properties.typeProperties.servicePrincipalId;
            linkedService.servicePrincipalKey = secretBaseToPasswordOrAKV(properties.typeProperties.servicePrincipalKey, true, this.context);
            linkedService.tenant = properties.typeProperties.tenant;
        }

        return linkedService;
    }
}
